/**
 * Loads YouTube's IFrame Player API, once per document.
 *
 * A plain `<iframe>` embed emits nothing — no play, no position, no completion — so
 * measuring how much of a video was watched requires this script and a real player
 * object. There is no lighter way to get the events.
 *
 * The script has to come from `www.youtube.com` even though the player itself is
 * hosted on the privacy-enhanced `youtube-nocookie.com` domain: that domain does not
 * serve `iframe_api`.
 */

export interface YouTubePlayer {
  getCurrentTime: () => number;
  getDuration: () => number;
  destroy: () => void;
}

export interface YouTubePlayerEvent {
  target: YouTubePlayer;
  data?: number;
}

export interface YouTubeApi {
  Player: new (
    element: HTMLElement,
    options: {
      videoId: string;
      host?: string;
      playerVars?: Record<string, string | number>;
      events?: {
        onReady?: (event: YouTubePlayerEvent) => void;
        onStateChange?: (event: YouTubePlayerEvent) => void;
        onError?: (event: YouTubePlayerEvent) => void;
      };
    },
  ) => YouTubePlayer;
  PlayerState: { ENDED: number; PLAYING: number };
}

declare global {
  interface Window {
    YT?: YouTubeApi;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const SCRIPT_SRC = "https://www.youtube.com/iframe_api";

let loader: Promise<YouTubeApi> | null = null;

export function loadYouTubeApi(): Promise<YouTubeApi> {
  if (loader) return loader;

  loader = new Promise<YouTubeApi>((resolve, reject) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }

    // Chain rather than overwrite: the global is a single callback slot, and a
    // second player mounting mid-load would otherwise orphan the first resolver.
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      if (window.YT) resolve(window.YT);
      else reject(new Error("youtube-api-unavailable"));
    };

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    if (existing) return;

    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onerror = () => reject(new Error("youtube-api-unavailable"));
    document.head.appendChild(script);
  });

  // A failed load must not be cached as a permanent failure: the member may simply
  // have been offline when the article first opened.
  loader.catch(() => {
    loader = null;
  });

  return loader;
}

/** Forget the cached loader. Tests only. */
export function resetYouTubeApiForTests(): void {
  loader = null;
}
